import React from "react";
import { motion } from "framer-motion";
import { Users, Trophy, Radio } from "lucide-react";
import { RECENT_HIRES, CHALLENGE_WINNERS, COMMUNITY_FEED } from "@/data/homepageData";

const Initials = ({ name }) => {
  const initials = (name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return <div className="w-9 h-9 rounded-full bg-[#5C9DFF]/15 border border-[#5C9DFF]/30 flex items-center justify-center text-[11px] font-bold text-[#5C9DFF] shrink-0">{initials}</div>;
};

/** Section 7 — Community: recent hires, challenge winners, and a live-feel activity feed.
 * ILLUSTRATIVE content — the platform doesn't track "who got hired" yet, same honesty
 * precedent used for the Global Leaderboard / Boss Battles mock content. */
export const CommunitySection = () => {
  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="community-section">
      <div className="max-w-2xl mb-12">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Community</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">You're joining thousands, not going it alone.</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="community-recent-hires">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-4">Recent Hires</p>
          <div className="space-y-3">
            {RECENT_HIRES.map((h) => (
              <div key={h.name} className="flex items-center gap-2.5">
                <Initials name={h.name} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{h.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{h.role} at {h.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="community-challenge-winners">
          <p className="text-xs font-bold uppercase tracking-wider text-violet-300 mb-4 flex items-center gap-1.5"><Trophy className="w-3 h-3" /> Challenge Winners</p>
          <div className="space-y-3">
            {CHALLENGE_WINNERS.map((w) => (
              <div key={w.name} className="flex items-center gap-2.5">
                <Initials name={w.name} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{w.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{w.badge} · {w.challenge}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="community-live-feed">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5C9DFF] mb-4 flex items-center gap-1.5"><Radio className="w-3 h-3" /> Live Feed</p>
          <div className="space-y-3">
            {COMMUNITY_FEED.map((f, i) => (
              <motion.div key={f.name + i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex items-start gap-2.5">
                <f.icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                <p className="text-[12px] text-slate-300 leading-snug"><span className="font-semibold text-white">{f.name}</span> {f.action} <span className="text-slate-600">· {f.time}</span></p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-600 mt-5">Illustrative community activity — your own progress and rank appear live the moment you join.</p>
    </section>
  );
};
